import {
  towingBenchmark,
  realWorldMpgCurve,
  TOWING_COMFORT_FACTOR,
  TOWING_GRADE_FACTOR,
} from "@/lib/proprietary-metrics";

/**
 * "Official Factory Specs vs. AutoSpecData Expert Benchmarks".
 * Server-rendered so crawlers and answer engines read our adjusted numbers,
 * which exist only here.
 */

export function TowingBenchmarkTable({
  fullName,
  officialLbs,
}: {
  fullName: string;
  officialLbs: number | null;
}) {
  const b = towingBenchmark(officialLbs);
  if (!b) return null;
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="spec-table">
        <thead>
          <tr>
            <th>Measurement</th>
            <th>Official factory spec</th>
            <th>AutoSpecData expert benchmark</th>
            <th>Shop note</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Max trailer weight</td>
            <td data-numeric>{b.officialLbs.toLocaleString()} lbs</td>
            <td data-numeric className="font-semibold text-primary">
              {b.safeLbs.toLocaleString()} lbs
            </td>
            <td>
              80% safety margin. Flat highway, properly equipped, brakes on the trailer.
            </td>
          </tr>
          <tr>
            <td>Sustained grade / heat</td>
            <td data-numeric>{b.officialLbs.toLocaleString()} lbs</td>
            <td data-numeric className="font-semibold text-primary">
              {b.gradeLbs.toLocaleString()} lbs
            </td>
            <td>70% margin for mountain passes, summer heat, and altitude.</td>
          </tr>
          <tr>
            <td>Tongue weight at safe limit</td>
            <td data-numeric>—</td>
            <td data-numeric className="font-semibold text-primary">
              ~{b.tongueLbs.toLocaleString()} lbs
            </td>
            <td>12% of the loaded trailer. Too light and it sways at 65 mph.</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground p-3">
        The {fullName} benchmark applies our 80% Towing Safety Margin
        ({Math.round(TOWING_COMFORT_FACTOR * 100)}% everyday /{" "}
        {Math.round(TOWING_GRADE_FACTOR * 100)}% grades) to the manufacturer's rating. It is a
        working recommendation, not a factory figure — the door placard and hitch label still win.
      </p>
    </div>
  );
}

export function RealWorldMpgTable({
  fullName,
  hwyMpg,
  vehicleClass,
}: {
  fullName: string;
  hwyMpg: number | null;
  vehicleClass: string | null;
}) {
  const rows = realWorldMpgCurve(hwyMpg, vehicleClass);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="spec-table">
        <thead>
          <tr>
            <th>Cruise speed</th>
            <th>EPA highway spec</th>
            <th>AutoSpecData adjusted MPG</th>
            <th>Fuel you lose</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.mph}>
              <td data-numeric>{r.mph} mph</td>
              <td data-numeric>{hwyMpg} MPG</td>
              <td data-numeric className="font-semibold text-primary">
                {r.mpg} MPG
              </td>
              <td data-numeric>{r.lossPct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground p-3">
        Our Real-World Speed MPG Adjuster re-scales the {fullName} EPA highway result with a
        quadratic drag term (mpg = hwy ÷ (1 + k·(v² − 60²)/60²), k = 0.55 for trucks, SUVs and vans,
        0.42 for cars). Wind, roof racks, and a heavy right foot make it worse, never better.
      </p>
    </div>
  );
}
